import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { Router } from '@angular/router';
import { CsControlsModule, CsIconModule } from '@cbuild/controls';
import { AuthenticationService, PropertyFilter } from '@cbuild/core';
import { StorageService } from '@cbuild/cs-common-data-services';
import {
  Feature,
  Module,
  PermissionComponent,
  SplitUrlPipe
} from '@cbuild/shared';
import { Subject, takeUntil } from 'rxjs';
import type { UserInfo } from '../../models/user-info.model';
import { NavMenuService } from '../nav-menu.service';

/**
 * @dynamic
 */
@Component({
  selector: 'cs-nav-profile-dropdown',
  templateUrl: './nav-profile-dropdown.component.html',
  styleUrls: ['./nav-profile-dropdown.component.scss'],
  animations: [
    trigger('navDropDown', [
      transition(':enter', [
        style({ opacity: 0, right: '-30px' }),
        animate('200ms ease-in-out', style({ opacity: 1, right: '*' }))
      ]),
      transition(':leave', [
        style({ opacity: 1, right: '*' }),
        animate('100ms ease-in-out', style({ opacity: 0, right: '-30px' }))
      ])
    ])
  ],
  imports: [CsControlsModule, CsIconModule, SplitUrlPipe, CommonModule]
})
export class NavProfileDropdownComponent
  extends PermissionComponent
  implements OnDestroy
{
  private _onDestroy$ = new Subject();
  public propertyLogo!: string;
  public propertyName!: string;
  public companyName!: string;

  @Input() public userInfo?: UserInfo;
  @Output()
  public propertyFilter = new EventEmitter<PropertyFilter>();
  @HostBinding('class.cs-nav-drop-down-opened')
  public displayUserDropDown = false;
  public opened: number | undefined = 1;
  public module = Module;
  public feature = Feature;
  private appliedPropertyFilter!: PropertyFilter;

  constructor(
    authenticationService: AuthenticationService,
    private storageService: StorageService,
    private router: Router,
    private navMenuService: NavMenuService
  ) {
    super(authenticationService);

    this.navMenuService.$openedDropDown
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(val => {
        if (this !== val) {
          this.closeDropDown();
        }
      });
  }

  public openPanel(i: number, event: Event): void {
    this.opened = this.opened !== i ? i : undefined;
    event.preventDefault();
    event.stopPropagation();
  }

  public logout(): void {
    this.authenticationService.logOut();
    this.storageService.removeItem('property');
    this.storageService.removeItem('page_options');
    this.storageService.removeItem('lead_filters');
    this.storageService.removeItem('payment_history_filters');
    this.storageService.removeItem('customer_filters');
    this.storageService.removeItem('campaign_leads_filters');
  }

  public async selectProperty(): Promise<void> {
    await this.router.navigate([], {
      queryParams: { property: undefined },
      queryParamsHandling: 'merge'
    });
  }

  public openDropDown(): void {
    this.navMenuService.$openedDropDown.next(this);
    this.displayUserDropDown = true;
  }

  public closeDropDown(): void {
    this.displayUserDropDown = false;
  }

  public ngOnDestroy(): void {
    this.closeDropDown();
  }

  public async resetRoute(): Promise<void> {
    await this.router.navigate([], {
      queryParams: { property: undefined },
      queryParamsHandling: 'merge'
    });
  }

  public resetFilters(): void {
    this.storageService.setItem('lead_filters', null);
  }

  public get selectedCompany(): number | null {
    return this.appliedPropertyFilter?.companyId;
  }

  public get selectedProject(): number | null {
    return this.appliedPropertyFilter?.propertyId;
  }

  public getImage(i: number): string {
    switch (i % 4) {
      case 0:
        return '/assets/images/project-1.png';
      case 1:
        return '/assets/images/project-2.png';
      case 2:
        return '/assets/images/project-3.png';
      case 3:
        return '/assets/images/project-4.png';
      default:
        return '/assets/images/project-1.png';
    }
  }
}
